import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { jwtDecode } from "jwt-decode";
import * as faceapi from 'face-api.js';
import '../../assets/css/update.css';

const Update = () => {
    const [formData, setFormData] = useState({
        empCode: '',
        name: '',
        category: '',
        email: '',
        password: '',
        divCode: '',
        projectCode: '',
        role: '',
        reportingManager: ''
    });
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const [faceDescriptors, setFaceDescriptors] = useState(null);
    const [updateMode, setUpdateMode] = useState("both"); 
    const fetchEmpRoleRef = useRef('');
    const token = localStorage.getItem("token");
    const videoRef = useRef(null);
    const canvasRef = useRef(null);

    let decoded = {};

    if (token) {
        try {
            decoded = jwtDecode(token);
        } catch (err) {
            console.error("Invalid token", err);
        }
    }

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const fetchEmployee = async () => {
        setError('');
        setMessage('');
        try {
            const res = await axios.get(`https://gannondunkerley.in/employees/emp/${formData.empCode}`, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem('token')}`
                }
            });

            const emp = res.data.emp;
            fetchEmpRoleRef.current = emp.role;

            if (emp.role === "powerUser" && decoded.role === "admin") {
                setMessage('You are not allowed to modify this User');
            } else {
                setFormData({
                    empCode: emp.empCode || '',
                    name: emp.name || '',
                    category: emp.category || '',
                    email: emp.email || '',
                    password: emp.password || '',
                    divCode: emp.divCode || '',
                    projectCode: emp.projectCode || '',
                    role: emp.role || '',
                    reportingManager: emp.reportingManager || ''
                });
                setMessage('Employee data loaded.');
            }
        } catch (err) {
            setError('Employee not found or error occurred.');
        }
    };

    // 🚀 FIXED NEURAL NETWORK INITIALIZATION HOOK FOR PRODUCTION
    useEffect(() => {
        const loadModels = async () => {
            try {
                // Generates explicit domain base path context natively
                const MODEL_URL = `${window.location.origin}/models`;
                console.log("✈️ Attempting to sync model binaries via update frame:", MODEL_URL);

                await Promise.all([
                    faceapi.nets.tinyFaceDetector.loadFromUri(MODEL_URL),
                    faceapi.nets.faceLandmark68Net.loadFromUri(MODEL_URL),
                    faceapi.nets.faceRecognitionNet.loadFromUri(MODEL_URL)
                ]);
                
                console.log("🎯 Face-API update-pipeline handshake models completely verified!");
                startVideoStream();
            } catch (error) {
                console.error('💥 Fatal layout breakdown loading models via update shell:', error);
            }
        };

        loadModels();
    }, []);

    const startVideoStream = () => {
        navigator.mediaDevices.getUserMedia({ video: {} })
            .then(stream => {
                if (videoRef.current) {
                    videoRef.current.srcObject = stream;
                }
            })
            .catch(err => {
                console.error('Error accessing the camera stream matrices:', err);
            });
    };

    const captureFace = async () => {
        if (!videoRef.current) return;

        let retries = 0;
        const maxRetries = 5;
        const minBoxWidth = 100;

        const dims = faceapi.matchDimensions(canvasRef.current, videoRef.current, true);
        const detect = async () => {
            const result = await faceapi.detectSingleFace(videoRef.current, new faceapi.TinyFaceDetectorOptions({ inputSize: 416, scoreThreshold: 0.5 }))
                .withFaceLandmarks()
                .withFaceDescriptor();

            if (result) {
                const resized = faceapi.resizeResults(result, dims);
                const boxWidth = resized.detection.box.width;

                if (boxWidth < minBoxWidth) {
                    console.warn("Face parameters too far away.");
                    retries++;
                    return false;
                }
                setFaceDescriptors(result.descriptor);
                alert("✅ Face captured successfully!");
                return true;
            }

            return false;
        };

        while (retries < maxRetries) {
            const detected = await detect();
            if (detected) break;
            retries++;
            await new Promise(resolve => setTimeout(resolve, 500));
        }

        if (retries >= maxRetries) {
            alert("❌ Face not detected. Please ensure lighting and alignment.");
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const { empCode, ...updateData } = formData;

        if (updateMode === "both") {
            if (!faceDescriptors) {
                alert("Please capture your face first!");
                return;
            }
            updateData.faceDescriptors = faceDescriptors;
        }

        if (updateMode === "dataOnly") {
            delete updateData.faceDescriptors;
        }

        if (fetchEmpRoleRef.current === "powerUser" && decoded.role === "admin") {
            setMessage('Failed to update employee.');
        } else {
            try {
                const res = await axios.put(`/employees/${empCode}`, updateData, {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem('token')}`
                    }
                });
                setMessage(res.data.msg);
                alert('Update successful.');
                window.location.href = "/UserUpdate";
            } catch (err) {
                setError('Failed to update employee.');
            }
        }
    };

    return (
        <div className='content'>
            <div className="update-form-container">
                <h2>Update Employee Data</h2>
                <div className="empcode-fetch">
                    <input
                        type="text"
                        name="empCode"
                        placeholder="Enter Employee Code"
                        value={formData.empCode}
                        onChange={handleChange}
                        required
                    />
                    <button onClick={fetchEmployee}>Fetch</button>
                </div>
                
                <div className="form-group">
                    <label>Update Mode:</label>
                    <select
                        value={updateMode}
                        onChange={(e) => setUpdateMode(e.target.value)}
                    >
                        <option value="both">Update Data + Face</option>
                        <option value="dataOnly">Only Data</option>
                    </select>
                </div>

                <form onSubmit={handleSubmit} className="update-form">
                    <label htmlFor="name">Full Name :</label>
                    <input type="text" name="name" value={formData.name} onChange={handleChange} />
                    
                    <label htmlFor="email">Email :</label>
                    <input type="text" name="email" value={formData.email} onChange={handleChange} />
                    
                    <label htmlFor="password">Password :</label>
                    <input type="text" name="password" value={formData.password} onChange={handleChange} />
                    
                    <label htmlFor="divCode">Division :</label>
                    <input type="text" name="divCode" value={formData.divCode} onChange={handleChange} />
                    
                    <label htmlFor="projectCode">Project Code :</label>
                    <input type="text" name="projectCode" value={formData.projectCode} onChange={handleChange} />

                    <div className="form-group">
                        <label>Role:</label>
                        {decoded.role === "powerUser" && (
                            <select name="role" value={formData.role} onChange={handleChange} required>
                                <option value="">Select Role</option>
                                <option value="User">User</option>
                                <option value="admin">Admin</option>
                                <option value="powerUser">Power User</option>
                            </select>
                        )}
                        {decoded.role === "admin" && (
                            <select name="role" value={formData.role} onChange={handleChange} required>
                                <option value="User">User</option>
                                <option value="admin">Admin</option>
                            </select>
                        )}
                    </div>

                    <div className="form-group">
                        <label>Category:</label>
                        <select
                            name="category"
                            value={formData.category}
                            onChange={handleChange}
                            required
                        >
                            <option value="">Select Category</option>
                            <option value="Employee">Employee</option>
                            <option value="MR Staff">MR Staff</option>
                            <option value="Labour">Labour</option>
                        </select>
                    </div>

                    <label htmlFor="reportingManager">Reporting Manager EmpCode :</label>
                    <input
                        type="text"
                        name="reportingManager"
                        value={formData.reportingManager}
                        onChange={handleChange}
                    />

                    {(updateMode === "both") && (
                        <div className="form-group">
                            <label>Capture Face:</label>
                            <div style={{ position: 'relative' }}>
                                <video ref={videoRef} autoPlay muted style={{ width: '100%' }} />
                                <canvas ref={canvasRef} style={{ position: 'absolute', top: 0, left: 0 }} />
                            </div>
                            <button type="button" onClick={captureFace} disabled={!!faceDescriptors}>
                                {faceDescriptors ? "✅ Face Captured" : "📸 Capture Face"}
                            </button>
                        </div>
                    )}

                    <button type="submit">Update Employee</button>
                </form>

                {message && <p className="update-message success">{message}</p>}
                {error && <p className="update-message error">{error}</p>}
            </div>
        </div>
    );
};

export default Update;